import React, { useState } from "react";

const FilterSidebar = ({
  filters,
  availableFilters,
  onFilterChange,
  onClearAll,
  hasActiveFilters,
  sortBy,
  onSortChange,
  section,
}) => {
  const isAccessoriesOnly = section === "accessories";
  const [expandedSections, setExpandedSections] = useState({
    sort: true,
    categories: true,
    tags: true,
    brand: false,
    rating: false,
    stock: true,
  });

  const sortOptions = [
    { value: "relevance", label: "Relevance" },
    { value: "newest", label: "Newest" },
    { value: "price_asc", label: "Price: Low to High" },
    { value: "price_desc", label: "Price: High to Low" },
    { value: "rating", label: "Customer Rating" },
    { value: "reviews", label: "Most Reviewed" },
    { value: "name_asc", label: "Name: A to Z" },
    { value: "name_desc", label: "Name: Z to A" },
  ];

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const FilterSection = ({ title, children, sectionKey, count = null }) => (
    <div className="border-b border-gray-200 pb-4">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="flex items-center justify-between w-full py-2 text-left"
      >
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-gray-900">{title}</h3>
          {count && (
            <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
              {count}
            </span>
          )}
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${
            expandedSections[sectionKey] ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {expandedSections[sectionKey] && (
        <div className="mt-3 space-y-2">{children}</div>
      )}
    </div>
  );

  const CheckboxFilter = ({ id, label, checked, onChange, count = null }) => (
    <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
      />
      <span className="text-sm text-gray-700 flex-1">{label}</span>
      {count && <span className="text-xs text-gray-500">({count})</span>}
    </label>
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      {/* Filter Sections */}
      <div className="space-y-6">
        {/* Sort Options */}
        <FilterSection title="Sort By" sectionKey="sort">
          <div className="space-y-2">
            {sortOptions.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded"
              >
                <input
                  type="radio"
                  name="sort"
                  value={option.value}
                  checked={sortBy === option.value}
                  onChange={(e) => onSortChange(e.target.value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* Categories Filter - hidden for accessories (only price + stock) */}
        {!isAccessoriesOnly &&
          availableFilters.categories &&
          availableFilters.categories.length > 0 && (
            <FilterSection
              title="Categories"
              sectionKey="categories"
              count={filters.categories.length || null}
            >
              <div className="max-h-48 overflow-y-auto space-y-2">
                {availableFilters.categories.map((category) => (
                  <CheckboxFilter
                    key={category.id}
                    id={`category-${category.id}`}
                    label={category.name}
                    checked={filters.categories.includes(category.id)}
                    onChange={(checked) => {
                      onFilterChange("categories", category.id);
                    }}
                  />
                ))}
              </div>
            </FilterSection>
          )}

        {/* Tags Filter - hidden for accessories */}
        {!isAccessoriesOnly &&
          availableFilters.tags &&
          availableFilters.tags.length > 0 && (
          <FilterSection
            title="Tags"
            sectionKey="tags"
            count={filters.tags.length || null}
          >
            <div className="max-h-48 overflow-y-auto space-y-2">
              {availableFilters.tags.map((tag) => (
                <CheckboxFilter
                  key={tag.id}
                  id={`tag-${tag.id}`}
                  label={
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </div>
                  }
                  checked={filters.tags.includes(tag.id)}
                  onChange={(checked) => {
                    onFilterChange("tags", tag.id);
                  }}
                />
              ))}
            </div>
          </FilterSection>
        )}

        {/* Brand Filter - hidden for accessories */}
        {!isAccessoriesOnly &&
          availableFilters.brands &&
          availableFilters.brands.length > 0 && (
          <FilterSection title="Brands" sectionKey="brand">
            <div className="max-h-48 overflow-y-auto space-y-2">
              {availableFilters.brands.map((brand) => (
                <label
                  key={brand}
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded"
                >
                  <input
                    type="radio"
                    name="brand"
                    value={brand}
                    checked={filters.brand === brand}
                    onChange={(e) => onFilterChange("brand", e.target.value)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-700">{brand}</span>
                </label>
              ))}
            </div>
            {filters.brand && (
              <button
                onClick={() => onFilterChange("brand", "")}
                className="text-xs text-blue-600 hover:text-blue-800 mt-2"
              >
                Clear brand filter
              </button>
            )}
          </FilterSection>
        )}

        {/* Rating Filter - hidden for accessories */}
        {!isAccessoriesOnly && (
        <FilterSection title="Rating" sectionKey="rating">
          <div className="space-y-2">
            {[4, 3, 2, 1].map((rating) => (
              <label
                key={rating}
                className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded"
              >
                <input
                  type="radio"
                  name="rating"
                  value={rating}
                  checked={filters.minRating === rating.toString()}
                  onChange={(e) => onFilterChange("minRating", e.target.value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                />
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`w-4 h-4 ${
                        i < rating ? "text-yellow-400" : "text-gray-300"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                  <span className="text-sm text-gray-600 ml-1">& up</span>
                </div>
              </label>
            ))}
          </div>
          {filters.minRating && (
            <button
              onClick={() => onFilterChange("minRating", "")}
              className="text-xs text-blue-600 hover:text-blue-800 mt-2"
            >
              Clear rating filter
            </button>
          )}
        </FilterSection>
        )}

        {/* Stock & Availability - for accessories: In Stock + Out of Stock only */}
        <FilterSection title="Availability" sectionKey="stock">
          <div className="space-y-3">
            <CheckboxFilter
              id="in-stock"
              label="In Stock Only"
              checked={filters.inStock}
              onChange={(checked) => onFilterChange("inStock", checked)}
            />
            <CheckboxFilter
              id="out-of-stock"
              label="Out of Stock Only"
              checked={filters.outOfStock}
              onChange={(checked) => onFilterChange("outOfStock", checked)}
            />
            {!isAccessoriesOnly && (
              <CheckboxFilter
                id="featured"
                label="Featured Products"
                checked={filters.featured}
                onChange={(checked) => onFilterChange("featured", checked)}
              />
            )}
          </div>
        </FilterSection>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Active Filters
          </h4>
          <div className="flex flex-wrap gap-2">
            {filters.search && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                Search: "{filters.search}"
                <button
                  onClick={() => onFilterChange("search", "")}
                  className="ml-1 hover:text-blue-900"
                >
                  ×
                </button>
              </span>
            )}
            {filters.categories.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                {filters.categories.length} Categories
                <button
                  onClick={() => onFilterChange("categories", [])}
                  className="ml-1 hover:text-green-900"
                >
                  ×
                </button>
              </span>
            )}
            {filters.tags.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                {filters.tags.length} Tags
                <button
                  onClick={() => onFilterChange("tags", [])}
                  className="ml-1 hover:text-purple-900"
                >
                  ×
                </button>
              </span>
            )}
            {(filters.minPrice || filters.maxPrice) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                Price: ₹{filters.minPrice || 0} - ₹{filters.maxPrice || "∞"}
                <button
                  onClick={() => {
                    onFilterChange("minPrice", "");
                    onFilterChange("maxPrice", "");
                  }}
                  className="ml-1 hover:text-yellow-900"
                >
                  ×
                </button>
              </span>
            )}
            {filters.outOfStock && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                Out of stock only
                <button
                  onClick={() => onFilterChange("outOfStock", false)}
                  className="ml-1 hover:text-gray-900"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterSidebar;
